import { type ThreeEvent, useFrame } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { createCurvedPlaneGeometry } from "./helpers";
import {
  BurnInAccumulationEffect,
  DynamicPassFragmentShader,
  StaticPassFragmentShader,
  VertexShader as vertexShader,
} from "./shaders";

interface CRTEffectProps {
  sourceTexture: THREE.Texture;
  width: number;
  height: number;
  renderer: THREE.WebGLRenderer;
  // Physical geometry curvature (3D bulge like CRT glass)
  curvature?: number;
  // Power state: 0 = off, 1 = on (animated transition)
  powerState?: number;
  // Power direction: 1 = turning on, -1 = turning off
  powerDirection?: number;
  // Channel switch flicker intensity: 0 = none, 1 = full flicker
  channelSwitchFlicker?: number;
  // Pointer event callbacks using UV coordinates (0-1 range)
  onPointerMoveUV?: (u: number, v: number) => void;
  onPointerClickUV?: (u: number, v: number) => void;
  onPointerLeave?: () => void;
}

export function CRTEffect({
  sourceTexture,
  width,
  height,
  renderer,
  curvature = 0,
  powerState = 1,
  powerDirection = 1,
  channelSwitchFlicker = 0,
  onPointerMoveUV,
  onPointerClickUV,
  onPointerLeave,
}: CRTEffectProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const lastFrameTimeRef = useRef(performance.now());
  const burnInIndexRef = useRef(0);

  // Extract UV from pointer event intersection
  const getUVFromEvent = useCallback(
    (e: ThreeEvent<PointerEvent | MouseEvent>) => {
      if (e.uv) {
        return { u: e.uv.x, v: e.uv.y };
      }
      return null;
    },
    [],
  );

  const handlePointerMove = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      const uv = getUVFromEvent(e);
      if (uv && onPointerMoveUV) {
        onPointerMoveUV(uv.u, uv.v);
      }
    },
    [getUVFromEvent, onPointerMoveUV],
  );

  const handlePointerClick = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      const uv = getUVFromEvent(e);
      if (uv && onPointerClickUV) {
        onPointerClickUV(uv.u, uv.v);
      }
    },
    [getUVFromEvent, onPointerClickUV],
  );

  const handlePointerLeave = useCallback(() => {
    onPointerLeave?.();
  }, [onPointerLeave]);

  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  const renderTargetWidth = width * dpr;
  const renderTargetHeight = height * dpr;

  // CRT settings
  const settings = useMemo(
    () => ({
      bloom: 0.55,
      brightness: 0.5,
      ambientLight: 0.25,
      flickering: 0.015,
      horizontalSync: 0.03,
      jitter: 0.08,
      staticNoise: 0.025,
      glowingLine: 0.08,
      burnIn: 0.12,
      chromaColor: 1,
      fontColor: new THREE.Vector3(1, 1, 1),
      backgroundColor: new THREE.Vector3(0.012, 0.012, 0.031),
      rasterizationMode: 1,
      rasterizationIntensity: 0.12,
      rgbShift: 0,
    }),
    [],
  );

  // Dynamically load noise texture (large base64 asset)
  const [noiseTexture, setNoiseTexture] = useState<THREE.Texture | null>(null);
  useEffect(() => {
    const mounted = true;
    if (!mounted) return;

    import("../../textures/noise").then((module) => {
      const textureLoader = new THREE.TextureLoader();
      const tex = textureLoader.load(module.NOISE_TEXTURE_BASE64);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      setNoiseTexture(tex);
    });
  }, []);

  // Create render targets
  const { staticRenderTarget, burnInTargets } = useMemo(() => {
    const staticTarget = new THREE.WebGLRenderTarget(
      renderTargetWidth,
      renderTargetHeight,
      {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
      },
    );

    const burnIn0 = new THREE.WebGLRenderTarget(
      renderTargetWidth,
      renderTargetHeight,
      {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
      },
    );

    const burnIn1 = new THREE.WebGLRenderTarget(
      renderTargetWidth,
      renderTargetHeight,
      {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
      },
    );

    return {
      staticRenderTarget: staticTarget,
      burnInTargets: [burnIn0, burnIn1] as [
        THREE.WebGLRenderTarget,
        THREE.WebGLRenderTarget,
      ],
    };
  }, [renderTargetWidth, renderTargetHeight]);

  // Create static pass material
  const staticMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        textTexture: { value: sourceTexture },
        fontColor: { value: settings.fontColor },
        backgroundColor: { value: settings.backgroundColor },
        chromaColor: { value: settings.chromaColor },
        rgbShift: { value: settings.rgbShift },
        bloom: { value: settings.bloom * 2.5 },
        brightness: { value: settings.brightness },
        resolution: {
          value: new THREE.Vector2(renderTargetWidth, renderTargetHeight),
        },
      },
      vertexShader,
      fragmentShader: StaticPassFragmentShader,
      transparent: false,
    });
  }, [sourceTexture, settings, renderTargetWidth, renderTargetHeight]);

  // Create burn-in material
  const burnInMaterial = useMemo(() => {
    const minBurnInFadeTime = 160;
    const maxBurnInFadeTime = 1600;
    const fadeTime =
      minBurnInFadeTime +
      (maxBurnInFadeTime - minBurnInFadeTime) * settings.burnIn;

    return new THREE.ShaderMaterial({
      uniforms: {
        currentSource: { value: staticRenderTarget.texture },
        burnInSource: { value: burnInTargets[1].texture },
        burnInTime: { value: 1.0 / fadeTime },
        deltaTime: { value: 0.016 },
      },
      vertexShader,
      fragmentShader: BurnInAccumulationEffect,
      transparent: false,
    });
  }, [staticRenderTarget, burnInTargets, settings.burnIn]);

  // Create dynamic pass material
  const dynamicMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        frameBuffer: { value: staticRenderTarget.texture },
        burnInSource: { value: burnInTargets[0].texture },
        textTexture: { value: sourceTexture },
        noiseSource: { value: noiseTexture },
        scaleNoiseSize: {
          value: new THREE.Vector2((width * 0.75) / 512, (height * 0.75) / 512),
        },
        fontColor: { value: settings.fontColor },
        backgroundColor: { value: settings.backgroundColor },
        chromaColor: { value: settings.chromaColor },
        ambientLight: { value: settings.ambientLight },
        resolution: {
          value: new THREE.Vector2(renderTargetWidth, renderTargetHeight),
        },
        time: { value: 0.0 },
        flickering: { value: settings.flickering },
        horizontalSync: { value: settings.horizontalSync },
        jitter: { value: settings.jitter },
        staticNoise: { value: settings.staticNoise },
        glowingLine: { value: settings.glowingLine * 0.2 },
        burnIn: { value: settings.burnIn },
        rasterizationMode: { value: settings.rasterizationMode },
        rasterizationIntensity: { value: settings.rasterizationIntensity },
        virtualResolution: { value: new THREE.Vector2(width, height) },
        powerState: { value: 1.0 },
        powerDirection: { value: 1.0 },
        channelSwitchFlicker: { value: 0.0 },
      },
      vertexShader,
      fragmentShader: DynamicPassFragmentShader,
      transparent: false,
    });
  }, [
    staticRenderTarget,
    burnInTargets,
    sourceTexture,
    noiseTexture,
    settings,
    width,
    height,
    renderTargetWidth,
    renderTargetHeight,
  ]);

  // Create scenes and cameras for multi-pass rendering
  const { staticScene, staticCamera, burnInScene, burnInCamera } =
    useMemo(() => {
      const sScene = new THREE.Scene();
      const sCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
      sCamera.position.z = 1;

      const staticGeometry = new THREE.PlaneGeometry(2, 2);
      const staticMesh = new THREE.Mesh(staticGeometry, staticMaterial);
      sScene.add(staticMesh);

      const bScene = new THREE.Scene();
      const bCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
      bCamera.position.z = 1;

      const burnInGeometry = new THREE.PlaneGeometry(2, 2);
      const burnInMesh = new THREE.Mesh(burnInGeometry, burnInMaterial);
      bScene.add(burnInMesh);

      return {
        staticScene: sScene,
        staticCamera: sCamera,
        burnInScene: bScene,
        burnInCamera: bCamera,
      };
    }, [staticMaterial, burnInMaterial]);

  // Update source texture reference
  useEffect(() => {
    if (staticMaterial.uniforms.textTexture) {
      staticMaterial.uniforms.textTexture.value = sourceTexture;
    }
    if (dynamicMaterial.uniforms.textTexture) {
      dynamicMaterial.uniforms.textTexture.value = sourceTexture;
    }
  }, [sourceTexture, staticMaterial, dynamicMaterial]);

  // Update uniforms on resize
  useEffect(() => {
    const newWidth = width * dpr;
    const newHeight = height * dpr;

    staticRenderTarget.setSize(newWidth, newHeight);
    burnInTargets[0].setSize(newWidth, newHeight);
    burnInTargets[1].setSize(newWidth, newHeight);

    if (staticMaterial.uniforms.resolution) {
      staticMaterial.uniforms.resolution.value.set(newWidth, newHeight);
    }
    if (dynamicMaterial.uniforms.resolution) {
      dynamicMaterial.uniforms.resolution.value.set(newWidth, newHeight);
    }
    if (dynamicMaterial.uniforms.scaleNoiseSize) {
      dynamicMaterial.uniforms.scaleNoiseSize.value.set(
        (width * 0.75) / 512,
        (height * 0.75) / 512,
      );
    }
    if (dynamicMaterial.uniforms.virtualResolution) {
      dynamicMaterial.uniforms.virtualResolution.value.set(width, height);
    }
  }, [
    width,
    height,
    dpr,
    staticRenderTarget,
    burnInTargets,
    staticMaterial,
    dynamicMaterial,
  ]);

  // Animation frame
  useFrame(() => {
    const currentTime = performance.now();
    const deltaTime = (currentTime - lastFrameTimeRef.current) / 1000.0;
    lastFrameTimeRef.current = currentTime;

    // Update time uniform
    if (dynamicMaterial.uniforms.time) {
      dynamicMaterial.uniforms.time.value = currentTime % 100000;
    }

    // Update power state uniforms
    if (dynamicMaterial.uniforms.powerState) {
      dynamicMaterial.uniforms.powerState.value = powerState;
    }
    if (dynamicMaterial.uniforms.powerDirection) {
      dynamicMaterial.uniforms.powerDirection.value = powerDirection;
    }
    if (dynamicMaterial.uniforms.channelSwitchFlicker) {
      dynamicMaterial.uniforms.channelSwitchFlicker.value =
        channelSwitchFlicker;
    }

    // Render static pass
    renderer.setRenderTarget(staticRenderTarget);
    renderer.render(staticScene, staticCamera);

    // Render burn-in accumulation
    if (settings.burnIn > 0) {
      if (burnInMaterial.uniforms.deltaTime) {
        burnInMaterial.uniforms.deltaTime.value = deltaTime;
      }

      const readIndex = burnInIndexRef.current;
      const writeIndex = 1 - burnInIndexRef.current;

      if (burnInMaterial.uniforms.burnInSource) {
        burnInMaterial.uniforms.burnInSource.value =
          burnInTargets[readIndex]?.texture;
      }
      if (burnInMaterial.uniforms.currentSource) {
        burnInMaterial.uniforms.currentSource.value =
          staticRenderTarget.texture;
      }
      if (burnInTargets[writeIndex]) {
        renderer.setRenderTarget(burnInTargets[writeIndex]);
      }
      renderer.render(burnInScene, burnInCamera);

      if (dynamicMaterial.uniforms.burnInSource) {
        dynamicMaterial.uniforms.burnInSource.value =
          burnInTargets[writeIndex]?.texture;
      }
      burnInIndexRef.current = writeIndex;
    }

    renderer.setRenderTarget(null);
  });

  // Cleanup
  useEffect(() => {
    return () => {
      staticRenderTarget.dispose();
      burnInTargets[0].dispose();
      burnInTargets[1].dispose();
      staticMaterial.dispose();
      dynamicMaterial.dispose();
      burnInMaterial.dispose();
      noiseTexture?.dispose();
    };
  }, [
    staticRenderTarget,
    burnInTargets,
    staticMaterial,
    dynamicMaterial,
    burnInMaterial,
    noiseTexture,
  ]);

  // Create curved geometry for physical CRT glass bulge
  const curvedGeometry = useMemo(() => {
    return createCurvedPlaneGeometry(2, 2, curvature);
  }, [curvature]);

  // Create glass geometry (same curvature but slightly forward)
  const glassGeometry = useMemo(() => {
    return createCurvedPlaneGeometry(2, 2, curvature);
  }, [curvature]);

  return (
    <group>
      {/* CRT Screen with effects */}
      <mesh
        ref={meshRef}
        position={[0, 0, 0]}
        onPointerMove={handlePointerMove}
        onPointerOver={handlePointerMove}
        onClick={handlePointerClick}
        onPointerOut={handlePointerLeave}
        onPointerLeave={handlePointerLeave}
      >
        <primitive object={curvedGeometry} attach="geometry" />
        <primitive object={dynamicMaterial} attach="material" />
      </mesh>

      {/* Reflective glass pane overlay */}
      <mesh position={[0, 0, 0.02]}>
        <primitive object={glassGeometry} attach="geometry" />
        <meshPhysicalMaterial
          transparent
          opacity={0.08}
          roughness={0.05}
          metalness={0.1}
          reflectivity={0.9}
          clearcoat={1}
          clearcoatRoughness={0.1}
          envMapIntensity={0.5}
          color="#ffffff"
          side={THREE.FrontSide}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}
